import React, {useEffect, useMemo, useState} from "react";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import {DefaultPageProps} from "@models/DefaultProps.ts";
import {IOrcamentoFiltroDto} from "@models/solicitacoes/orcamentoDto.ts";
import {Button, ButtonGroup, Chip, FormControl, Grid, InputLabel, MenuItem, Select, Tooltip} from "@mui/material";
import {StatusAprovacaoOrcamento, StatusAprovacaoOrcamentoValues} from "@models/solicitacoes/orcamento.ts";
import {SaveOutlined} from "@mui/icons-material";
import {IFlag} from "@models/shared/IFlag.ts";
import {FlagsPesquisa} from "@components/flags/FlagsPesquisa.tsx";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {IFlagValueFilter} from "@components/shared/dto/IFlagValueFilter.ts";
import {SelectNullableBool} from "@shared/specialComponents/SelectNullableBool.tsx";

interface OrcamentosPageFilters extends DefaultPageProps<IOrcamentoFiltroDto> {
    onApply: (filtros: IOrcamentoFiltroDto) => void;
}

export function OrcamentosPageFilters(props: OrcamentosPageFilters) {
    const {createAlert} = useAlert()

    const [selectedFlags, setSelectedFlags] = useState<IFlagValueFilter[]>(props.filtersProps?.filters?.flags ?? [])
    const [filtros, setFiltros] = useState<IOrcamentoFiltroDto & {
        flags?: IFlagValueFilter[]
    }>({...props.filtersProps?.filters});

    const hasFilterApplyied = useMemo(() => {
        return filtros != null && Object.keys(filtros).length > 0
    }, [])

    const addFlag = (flag?: IFlag) => {
        if (flag == null) return;
        if (selectedFlags.find(x => x.nome == flag.nome)) {
            createAlert('Flag já está na lista', 'error', 2000);
            return;
        }
        setSelectedFlags([...selectedFlags, {nome: flag.nome, id: flag.id, valor: undefined}]);
    }

    const removeFlag = (flag: IFlagValueFilter) => {
        setSelectedFlags(selectedFlags.filter(x => x.nome != flag.nome));
    }

    function getNextValue(flag: IFlagValueFilter/*, type: 'color' | 'bool'*/) {
        if (flag.valor == null) return true;
        if (flag.valor) return false;
        return undefined
    }

    function getFlagValue(flag: IFlagValueFilter, type: 'color'): 'primary' | 'secondary' | undefined;
    function getFlagValue(flag: IFlagValueFilter, type: 'label'): 'Qualquer' | 'Sim' | 'Não';
    function getFlagValue(flag: IFlagValueFilter, type: 'color' | 'label') {
        if (type == 'color') {
            if (flag.valor == null) return undefined;
            if (flag.valor) return 'primary';
            return 'secondary';
        }
        if (type == 'label') {
            if (flag.valor == null) return 'Qualquer';
            if (flag.valor) return 'Sim';
            return 'Não';
        }
    }

    const invertFlagValue = (flag: IFlagValueFilter) => {
        setSelectedFlags(selectedFlags.map(x => x.id == flag.id ? {...x, valor: getNextValue(flag)} : x));
    }

    type keysNullBools = keyof typeof filtros
    const nullbools = [{name: 'isConfirmado', friendDesc: 'Confirmados pelo solicitante'},
        {name: 'isAutorizado', friendDesc: 'Autorizado por um aprovador'},
        {name: 'isPago', friendDesc: 'Já pagos'},
        {name: 'isCancelado', friendDesc: 'Cancelados'}, {name: 'isComprado', friendDesc: 'Já comprados'},]

    useEffect(() => {
        setFiltros(prev => ({
            ...prev,
            flags: selectedFlags,
        }));

    }, [selectedFlags]);

    return (
        <DefaultContainer>
            <Grid container spacing={2} sx={{padding: 2}}>
                <Grid size={12}>
                    <Grid container justifyContent={'space-between'}>
                        <Grid></Grid>
                        <Grid component={ButtonGroup}>
                            <Tooltip
                                title={'Isso salva no seu navegador, e na proxima vez que você abrir a pagina, terá os mesmos filtros'}>
                                <Button variant={'outlined'}
                                        color={'secondary'}
                                        endIcon={<SaveOutlined/>}
                                        onClick={() => {
                                            if (props.onApply) {
                                                props.onApply(filtros);
                                            }
                                            localStorage.setItem('orcamentos-default-filters', JSON.stringify({
                                                flags: filtros.flags,
                                                status: filtros.status
                                            }));
                                        }}>Aplicar como padrão</Button>
                            </Tooltip>
                            <Button variant={'outlined'}
                                    endIcon={<SaveOutlined/>}
                                    onClick={() => {
                                        if (props.onApply) {
                                            props.onApply(filtros);
                                        }
                                    }}>Aplicar</Button>

                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={12}>
                    <FlagsPesquisa onItemSelected={addFlag} options={{clearOnSelect: true}}/>
                </Grid>
                <Grid size={12} container spacing={0.5}>
                    {selectedFlags.map(x => <Chip color={getFlagValue(x, 'color')} onClick={() => {
                        invertFlagValue(x)
                    }} label={`${x.nome} (${getFlagValue(x, 'label')})`} onDelete={() => removeFlag(x)}/>)}
                </Grid>

                <Grid size={12}>
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select label={'Status'}
                                multiple
                                value={filtros.status ?? []}
                                onChange={(e) => {
                                    const {target: {value}} = e;
                                    setFiltros({
                                        ...filtros,
                                        status: value as StatusAprovacaoOrcamento[],
                                    });

                                }}
                                size={'small'}
                                fullWidth
                        >
                            {StatusAprovacaoOrcamentoValues.map(x =>
                                <MenuItem value={x}>{x}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={12} spacing={1} container>
                    {nullbools.map(x => {
                        return (<SelectNullableBool value={filtros[x.name] as any}
                                                    label={x.friendDesc}
                                                    onChange={(e) => {
                                                        setFiltros(prev => ({...prev, [x.name]: e}))
                                                    }}/>)
                    })}

                </Grid>

            </Grid>
        </DefaultContainer>
    );
}